import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Grid, MenuItem, Paper, Stack, TextField,
  Typography, Table, TableBody, TableCell, TableHead, TableRow, Checkbox
} from '@mui/material';
import axios from 'axios';
import { FALLBACK_ACTIVE_SEASON } from '../../constants/seasons';
import { getIdToken } from '../Firebase';

const emptyBulkFields = {
  target_size: '',
  promotion_spots: '',
  promotion_playoff_spots: '',
  relegation_spots: '',
  relegation_playoff_spots: '',
  promoted_competition_id: '',
};

const DEFAULT_SOURCE_URLS = [
  'http://www.90minut.pl/',
  ...Array.from({ length: 16 }, (_, index) => `http://www.90minut.pl/ligireg-${index + 1}.html`)
].join('\n');

const API_HEADERS = {
  'Content-Type': 'application/json',
  'X-API-Key': process.env.REACT_APP_MAKE_API_KEY,
};

const authConfig = async (config = {}) => {
  const token = await getIdToken();
  return {
    ...config,
    headers: {
      ...(config.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
};

const numberColumnSx = {
  width: 120,
  minWidth: 58,
  maxWidth: 140,
  px: 0.25,
  textAlign: 'center'
};

const deduplicateCandidates = (candidates = []) => Array.from(
  candidates.reduce((byLeagueUrl, candidate) => {
    const key = candidate.league_url || `candidate:${candidate.id}`;
    const current = byLeagueUrl.get(key);
    if (!current || (!current.created_competition_id && candidate.created_competition_id)) {
      byLeagueUrl.set(key, candidate);
    }
    return byLeagueUrl;
  }, new Map()).values()
);

const numericInputStyle = {
  width: 48,
  height: 30,
  boxSizing: 'border-box',
  border: '1px solid rgba(255,255,255,0.24)',
  borderRadius: 4,
  background: 'transparent',
  color: 'inherit',
  textAlign: 'center',
  font: 'inherit',
  padding: '0 2px'
};

const SeasonImportManager = () => {
  const [season, setSeason] = useState(FALLBACK_ACTIVE_SEASON);
  const [sourceUrls, setSourceUrls] = useState(DEFAULT_SOURCE_URLS);
  const [leagueUrls, setLeagueUrls] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [data, setData] = useState({ batches: [], candidates: [], regions: [], districts: [] });
  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
  const [teamResolutionSelections, setTeamResolutionSelections] = useState({});
  const [teamSearchOptions, setTeamSearchOptions] = useState([]);
  const [missingTeamItems, setMissingTeamItems] = useState([]);
  const [bulkFields, setBulkFields] = useState(emptyBulkFields);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const importedCandidates = useMemo(
    () => data.candidates.filter((candidate) => candidate.created_competition_id),
    [data.candidates]
  );
  const seasonCandidates = useMemo(
    () => data.candidates.filter((candidate) => candidate.detected_season === season),
    [data.candidates, season]
  );
  const statusOptions = useMemo(
    () => Array.from(new Set(seasonCandidates.map((candidate) => candidate.status).filter(Boolean))).sort(),
    [seasonCandidates]
  );
  const regionOptions = useMemo(
    () => data.regions || [],
    [data.regions]
  );
  const visibleCandidates = useMemo(
    () => seasonCandidates.filter((candidate) =>
      (statusFilter === 'all' || candidate.status === statusFilter) &&
      (regionFilter === 'all' || Number(candidate.region_id) === Number(regionFilter))
    ),
    [seasonCandidates, statusFilter, regionFilter]
  );
  const allVisibleSelected = visibleCandidates.length > 0 &&
    visibleCandidates.every((candidate) => selectedCandidateIds.includes(candidate.id));
  const selectedImportedCompetitionIds = useMemo(
    () => data.candidates
      .filter((candidate) => selectedCandidateIds.includes(candidate.id) && candidate.created_competition_id)
      .map((candidate) => candidate.created_competition_id),
    [data.candidates, selectedCandidateIds]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/season-import`, await authConfig({
        params: { season }
      }));
      setData({
        ...response.data,
        candidates: deduplicateCandidates(response.data.candidates)
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [season]);

  useEffect(() => {
    load();
  }, [load]);

  const splitLines = (value) => value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const discover = async () => {
    setLoading(true);
    setMessage('');
    setError('');
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/season-import/discover`, {
        season,
        sourceUrls: splitLines(sourceUrls),
        leagueUrls: splitLines(leagueUrls)
      }, await authConfig());
      setMessage(`Batch #${response.data.batchId}: dodano ${response.data.inserted}, pominieto ${response.data.skipped}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateCandidate = async (candidate, patch) => {
    const next = { ...candidate, ...patch };
    setData((prev) => ({
      ...prev,
      candidates: prev.candidates.map((item) => item.id === candidate.id ? next : item)
    }));
    await axios.put(`${process.env.REACT_APP_API_URL}/api/admin/season-import/candidates/${candidate.id}`, next, await authConfig());
  };

  const searchTeams = async (query) => {
    if (!query || query.trim().length < 2) {
      setTeamSearchOptions([]);
      return;
    }
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/season-import/teams`, await authConfig({
        params: { q: query.trim() }
      }));
      setTeamSearchOptions(response.data.teams || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const resolveTeam = async (candidate, teamName, action, targetTeamId = null) => {
    setLoading(true);
    setError('');
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/season-import/team-resolutions`, {
        candidateId: candidate.id,
        sourceName: teamName,
        action,
        targetTeamId
      }, await authConfig());
      setMessage(action === 'create_new'
        ? `Zapisano decyzje: utworz nowa druzyne ${teamName}`
        : `Zapisano decyzje: przypisz ${teamName}`);
      setMissingTeamItems((prev) => prev.filter((item) => !(item.candidate.id === candidate.id && item.teamName === teamName)));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const importCandidate = async (candidate) => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/season-import/candidates/${candidate.id}/import`, null, await authConfig());
      const createdTeams = response.data.createdTeams || [];
      setMessage(createdTeams.length
        ? `Utworzono lige competition_id=${response.data.competitionId}. Nowe druzyny: ${createdTeams.join(', ')}`
        : `Utworzono lige competition_id=${response.data.competitionId}`);
      await load();
    } catch (err) {
      const missing = err.response?.data?.missingTeams || [];
      if (missing.length) {
        setMissingTeamItems(missing.map((teamName) => ({ candidate, teamName })));
      }
      setError(missing.length
        ? `${err.response?.data?.error}. Braki: ${missing.join(', ')}`
        : err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteCompetition = async (candidate) => {
    if (!candidate.created_competition_id) return;
    const confirmed = window.confirm(`Usunac competition_id=${candidate.created_competition_id} razem z powiazaniami?`);
    if (!confirmed) return;

    setLoading(true);
    setError('');
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/admin/season-import/competitions/${candidate.created_competition_id}`, await authConfig());
      setMessage(`Usunieto competition_id=${candidate.created_competition_id}`);
      setSelectedCandidateIds((prev) => prev.filter((id) => id !== candidate.id));
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const applyBulkConfig = async () => {
    const fields = Object.fromEntries(
      Object.entries(bulkFields)
        .filter(([, value]) => value !== '')
        .map(([key, value]) => [key, Number(value)])
    );

    if (selectedCandidateIds.length === 0 || Object.keys(fields).length === 0) return;

    setLoading(true);
    setError('');
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/admin/season-import/bulk-config`, {
        competitionIds: selectedImportedCompetitionIds,
        candidateIds: selectedCandidateIds,
        fields
      }, await authConfig());
      setMessage(`Zaktualizowano kandydatow: ${response.data.updatedCandidates}, competitions: ${response.data.updatedCompetitions}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const apiCall = async (endpoint, data = {}, successText = 'Gotowe') => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}${endpoint}`, data, { headers: API_HEADERS });
      setMessage(response.data?.message || successText);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompetitionData = (competitionId) => apiCall(
    '/api/scrap-data',
    { competitionId },
    `Pobrano dane dla competition_id=${competitionId}`
  );

  const runBootstrapSimulation = (competitionId) => apiCall(
    '/api/simulate',
    { competitionId, bootstrapInitialSimulation: true, skipCascadeAnalyzer: true },
    `Uruchomiono symulacje dla competition_id=${competitionId}`
  );

  const toggleCandidate = (candidateId) => {
    setSelectedCandidateIds((prev) =>
      prev.includes(candidateId)
        ? prev.filter((id) => id !== candidateId)
        : [...prev, candidateId]
    );
  };

  const toggleVisibleCandidates = () => {
    const visibleIds = visibleCandidates.map((candidate) => candidate.id);
    setSelectedCandidateIds((prev) => {
      if (visibleIds.length > 0 && visibleIds.every((id) => prev.includes(id))) {
        return prev.filter((id) => !visibleIds.includes(id));
      }
      return Array.from(new Set([...prev, ...visibleIds]));
    });
  };

  const importSelectedCandidates = async () => {
    const candidatesToImport = data.candidates.filter(
      (candidate) => selectedCandidateIds.includes(candidate.id) && !candidate.created_competition_id
    );
    for (const candidate of candidatesToImport) {
      await importCandidate(candidate);
    }
  };

  const numberField = (candidate, field, label, width = 96) => (
    <TextField
      type="number"
      size="small"
      label={label}
      value={candidate[field] ?? ''}
      onChange={(e) => updateCandidate(candidate, { [field]: e.target.value })}
      sx={{ width }}
    />
  );

  const updateTeamNames = (candidate, value) => {
    const teamNames = splitLines(value);
    updateCandidate(candidate, {
      team_names: teamNames,
      team_count: teamNames.length,
      status: candidate.created_competition_id ? candidate.status : 'needs_review'
    });
  };

  return (
    <Box sx={{ p: 3, width: '100%', boxSizing: 'border-box' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Nowy sezon</Typography>
          <Typography variant="body2" color="text.secondary">Kontrolowany import lig 2026/27 z 90minut.pl</Typography>
        </Box>
        <Button variant="outlined" onClick={load} disabled={loading}>Odswiez</Button>
      </Stack>

      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 3, bgcolor: '#1e1e1e' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2}>
            <TextField fullWidth label="Sezon" value={season} onChange={(e) => setSeason(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth multiline minRows={3}
              label="Strony katalogowe 90minut"
              value={sourceUrls}
              onChange={(e) => setSourceUrls(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth multiline minRows={3}
              label="Pojedyncze linki lig (opcjonalnie)"
              value={leagueUrls}
              onChange={(e) => setLeagueUrls(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField select fullWidth label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="all">Wszystkie</MenuItem>
              {statusOptions.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField select fullWidth label="Region" value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)}>
              <MenuItem value="all">Wszystkie</MenuItem>
              {regionOptions.map((region) => <MenuItem key={region.id} value={region.id}>{region.name} ({region.id})</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={2}>
              <Button variant="contained" onClick={discover} disabled={loading}>
                {loading ? <CircularProgress size={20} /> : 'Szukaj kandydatow'}
              </Button>
              <Button variant="outlined" onClick={importSelectedCandidates} disabled={loading || selectedCandidateIds.length === 0}>
                Importuj zaznaczone
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, mb: 3, bgcolor: '#1e1e1e' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Hurtowa konfiguracja zaznaczonych lig</Typography>
        <Grid container spacing={2}>
          {Object.keys(emptyBulkFields).map((field) => (
            <Grid item xs={12} sm={6} md={2} key={field}>
              <TextField
                fullWidth type="number" size="small" label={field}
                value={bulkFields[field]}
                onChange={(e) => setBulkFields((prev) => ({ ...prev, [field]: e.target.value }))}
              />
            </Grid>
          ))}
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="outlined" onClick={applyBulkConfig} disabled={loading || selectedCandidateIds.length === 0}>
              Zastosuj
            </Button>
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary">
          Zaznaczone: {selectedCandidateIds.length}. Competition_id: {selectedImportedCompetitionIds.join(', ') || 'brak'}
        </Typography>
      </Paper>

      <Paper sx={{ overflow: 'auto', bgcolor: '#1e1e1e' }}>
        <Table size="small" sx={{
          width: '100%',
          minWidth: 1780,
          '& th:nth-of-type(5), & th:nth-of-type(6), & th:nth-of-type(7), & td:nth-of-type(5), & td:nth-of-type(6), & td:nth-of-type(7)': numberColumnSx
        }}>
          <colgroup>
            <col style={{ width: 44 }} />
            <col style={{ width: 115 }} />
            <col style={{ width: 360 }} />
            <col style={{ width: 92 }} />
            <col style={{ width: 70 }} />
            <col style={{ width: 220 }} />
            <col style={{ width: 240 }} />
            <col style={{ width: 220 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 290 }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell>
                <Checkbox
                  checked={allVisibleSelected}
                  indeterminate={!allVisibleSelected && visibleCandidates.some((candidate) => selectedCandidateIds.includes(candidate.id))}
                  onChange={toggleVisibleCandidates}
                />
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Liga</TableCell>
              <TableCell>Sezon</TableCell>
              <TableCell sx={numberColumnSx}>Poz.</TableCell>
              <TableCell sx={numberColumnSx}>Reg.</TableCell>
              <TableCell sx={numberColumnSx}>Okr.</TableCell>
              <TableCell>Druzyny</TableCell>
              <TableCell>Link</TableCell>
              <TableCell>Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleCandidates.map((candidate) => (
              <React.Fragment key={candidate.id}>
                <TableRow hover>
                  <TableCell>
                    <Checkbox
                      checked={selectedCandidateIds.includes(candidate.id)}
                      onChange={() => toggleCandidate(candidate.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={candidate.status} color={candidate.detected_season === season ? 'success' : 'warning'} />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth size="small" value={candidate.name || ''}
                      onChange={(e) => updateCandidate(candidate, { name: e.target.value, status: 'needs_review' })}
                    />
                    <TextField
                      fullWidth size="small" label="Grupa" value={candidate.group || ''}
                      onChange={(e) => updateCandidate(candidate, { group: e.target.value, status: 'needs_review' })}
                      sx={{ mt: 1 }}
                    />
                  </TableCell>
                  <TableCell>{candidate.detected_season || 'brak'}</TableCell>
                  <TableCell sx={numberColumnSx}>
                    <input
                      type="number"
                      value={candidate.level || ''}
                      style={numericInputStyle}
                      onChange={(e) => updateCandidate(candidate, { level: Number(e.target.value), status: 'needs_review' })}
                    />
                  </TableCell>
                  <TableCell sx={numberColumnSx}>
                    <TextField
                      select
                      size="small"
                      value={candidate.region_id ?? ''}
                      onChange={(e) => updateCandidate(candidate, { region_id: Number(e.target.value), status: 'needs_review' })}
                      sx={{ width: 190 }}
                    >
                      <MenuItem value="">-</MenuItem>
                      {(data.regions || []).map((region) => (
                        <MenuItem key={region.id} value={region.id}>{region.name} ({region.id})</MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell sx={numberColumnSx}>
                    <TextField
                      select
                      size="small"
                      value={candidate.district_id ?? ''}
                      onChange={(e) => updateCandidate(candidate, { district_id: Number(e.target.value), status: 'needs_review' })}
                      sx={{ width: 210 }}
                    >
                      <MenuItem value="">-</MenuItem>
                      {(data.districts || []).map((district) => (
                        <MenuItem key={district.id} value={district.id}>{district.name} ({district.id})</MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden' }}>
                    <details>
                      <summary>{candidate.team_count} druzyn</summary>
                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        maxRows={8}
                        size="small"
                        defaultValue={(candidate.team_names || []).join('\n')}
                        onBlur={(e) => updateTeamNames(candidate, e.target.value)}
                        sx={{ mt: 1, minWidth: 260 }}
                      />
                    </details>
                  </TableCell>
                  <TableCell>
                    <a href={candidate.league_url} target="_blank" rel="noreferrer">90minut</a>
                  </TableCell>
                  <TableCell>
                    {candidate.created_competition_id ? (
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={`ID ${candidate.created_competition_id}`} />
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={() => fetchCompetitionData(candidate.created_competition_id)}
                          disabled={loading}
                        >
                          Pobierz dane
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="secondary"
                          onClick={() => runBootstrapSimulation(candidate.created_competition_id)}
                          disabled={loading}
                        >
                          Symulacja
                        </Button>
                        <Button size="small" color="error" variant="outlined" onClick={() => deleteCompetition(candidate)} disabled={loading}>
                          Usun
                        </Button>
                      </Stack>
                    ) : (
                      <Button size="small" variant="contained" onClick={() => importCandidate(candidate)} disabled={loading}>
                        Importuj
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell />
                  <TableCell colSpan={9} sx={{ pt: 0, pb: 2, borderBottom: '1px solid rgba(255,255,255,0.14)' }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                      {numberField(candidate, 'target_size', 'target')}
                      {numberField(candidate, 'promotion_spots', 'awans')}
                      {numberField(candidate, 'promotion_playoff_spots', 'baraz aw.')}
                      {numberField(candidate, 'relegation_spots', 'spadek')}
                      {numberField(candidate, 'relegation_playoff_spots', 'baraz sp.')}
                      {numberField(candidate, 'promoted_competition_id', 'promoted_id', 118)}
                      {candidate.created_competition_id && (
                        <Chip size="small" color="info" variant="outlined" label="edytuje competitions" />
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {missingTeamItems.length > 0 && (
        <Paper sx={{ p: 2, mt: 3, bgcolor: '#1e1e1e' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Brakujace druzyny do rozstrzygniecia</Typography>
          <Stack spacing={1.5}>
            {missingTeamItems.map(({ candidate, teamName }) => {
              const key = `${candidate.id}:${teamName}`;
              return (
                <Stack key={key} direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Box sx={{ minWidth: 260 }}>
                    <Typography variant="body2" fontWeight={700}>{teamName}</Typography>
                    <Typography variant="caption" color="text.secondary">{candidate.name} {candidate.group ? `- ${candidate.group}` : ''}</Typography>
                  </Box>
                  <Autocomplete
                    size="small"
                    options={teamSearchOptions}
                    getOptionLabel={(option) => option ? `${option.name} (${option.id})` : ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={teamResolutionSelections[key] || null}
                    onInputChange={(_, value) => searchTeams(value)}
                    onChange={(_, value) => setTeamResolutionSelections((prev) => ({ ...prev, [key]: value }))}
                    sx={{ width: 360 }}
                    renderInput={(params) => <TextField {...params} label="Istniejaca druzyna" />}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={loading || !teamResolutionSelections[key]}
                    onClick={() => resolveTeam(candidate, teamName, 'assign_existing', teamResolutionSelections[key]?.id)}
                  >
                    Przypisz i zmien nazwe
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="warning"
                    disabled={loading}
                    onClick={() => resolveTeam(candidate, teamName, 'create_new')}
                  >
                    Utworz nowa
                  </Button>
                </Stack>
              );
            })}
          </Stack>
        </Paper>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Zaimportowane: {importedCandidates.length} / {data.candidates.length}. Discovery nie zapisuje lig do competitions bez klikniecia Importuj.
      </Typography>
    </Box>
  );
};

export default SeasonImportManager;
